// src/pages/CartPage.jsx
import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import Icon from "@mdi/react";
import {
  mdiMapMarkerOutline,
  mdiFlagOutline,
  mdiCalendarMonthOutline,
  mdiClockOutline,
  mdiMapMarkerPath,
  mdiClose,
} from "@mdi/js";
import { useUser, ROLES } from "../context/UserContext";
import {
  getCartForCurrentUser,
  removeCartItemByIdForCurrentUser,
  updateCartItemQuantityForCurrentUser,
} from "../api/cartApi";
import { getSearchResultsConfig } from "../api/vehicleApi";
import carDetails from "../data/carDetails.json";
import CheckoutBar from "../components/CheckoutBar";
import VehicleImage from "../components/common/VehicleImage";
import { summarizeScheduledBookingAmounts } from "../lib/scheduledBookingPricing";

/* ───────── Image helpers ───────── */
function makeImageIndex(details) {
  const idx = Object.create(null);
  for (const brand of Object.keys(details || {})) {
    const b = details[brand];
    if (!b?.cars) continue;
    idx[brand] = Object.create(null);
    for (const m of b.cars) {
      const colorMap = Object.create(null);
      if (m?.colorImages && typeof m.colorImages === "object") {
        for (const c of Object.keys(m.colorImages)) colorMap[c] = m.colorImages[c];
      } else if (Array.isArray(m?.colors)) {
        for (const c of m.colors) if (c?.name && c?.image) colorMap[c.name] = c.image;
      }
      idx[brand][m.name] = { colorMap, modelImage: m.image || null };
    }
  }
  return idx;
}
function getImage(index, brand, model, color) {
  const b = index?.[brand];
  const m = b?.[model];
  if (!m) return "/images/default.jpg";
  return m.colorMap?.[color] || m.modelImage || "/images/default.jpg";
}

function pickFirst(...values) {
  for (const value of values) {
    if (value !== null && value !== undefined && value !== "") return value;
  }
  return undefined;
}

function resolveCartVehicle(item) {
  const vehicle = item?.vehicle || {};
  const vehicleSummary = item?.vehicleSummary || {};
  const vehicleInfo = item?.vehicleInfo || {};
  const vehicleDetails = item?.vehicleDetails || {};

  return {
    registrationNumber: pickFirst(
      vehicle.registrationNumber,
      vehicleSummary.registrationNumber,
      vehicleInfo.registrationNumber,
      vehicleDetails.registrationNumber,
      item?.registrationNumber,
      item?.vehicleRegistrationNumber
    ),
    brand: pickFirst(
      vehicle.brand,
      vehicleSummary.brand,
      vehicleInfo.brand,
      vehicleDetails.brand,
      item?.vehicleBrand,
      item?.catalogBrand,
      item?.carBrand,
      item?.brand
    ),
    model: pickFirst(
      vehicle.model,
      vehicleSummary.model,
      vehicleInfo.model,
      vehicleDetails.model,
      item?.vehicleModel,
      item?.catalogModel,
      item?.carModel,
      item?.model
    ),
    type: pickFirst(
      vehicle.type,
      vehicleSummary.type,
      vehicleInfo.type,
      vehicleDetails.type,
      item?.vehicleType,
      item?.catalogType,
      item?.carType,
      item?.type
    ),
    seatingCapacity: pickFirst(
      vehicle.seatingCapacity,
      vehicle.seatCount,
      vehicle.seats,
      vehicleSummary.seatingCapacity,
      vehicleSummary.seatCount,
      vehicleSummary.seats,
      vehicleInfo.seatingCapacity,
      vehicleInfo.seatCount,
      vehicleInfo.seats,
      vehicleDetails.seatingCapacity,
      vehicleDetails.seatCount,
      vehicleDetails.seats,
      item?.seatCount,
      item?.seats,
      item?.seatingCapacity
    ),
    fuelType: pickFirst(
      vehicle.fuelType,
      vehicleSummary.fuelType,
      vehicleInfo.fuelType,
      vehicleDetails.fuelType,
      item?.fuelType
    ),
    color: pickFirst(
      vehicle.color,
      vehicleSummary.color,
      vehicleInfo.color,
      vehicleDetails.color,
      item?.vehicleColor,
      item?.catalogColor,
      item?.carColor,
      item?.color
    ),
    yearOfPurchase: pickFirst(
      vehicle.yearOfPurchase,
      vehicle.year,
      vehicle.manufacturingYear,
      vehicle.modelYear,
      vehicleSummary.yearOfPurchase,
      vehicleSummary.year,
      vehicleSummary.manufacturingYear,
      vehicleSummary.modelYear,
      vehicleInfo.yearOfPurchase,
      vehicleInfo.year,
      vehicleInfo.manufacturingYear,
      vehicleInfo.modelYear,
      vehicleDetails.yearOfPurchase,
      vehicleDetails.year,
      vehicleDetails.manufacturingYear,
      vehicleDetails.modelYear,
      item?.yearOfPurchase,
      item?.year,
      item?.manufacturingYear,
      item?.modelYear
    ),
    imageUrl: pickFirst(
      vehicle.imageUrl,
      vehicleSummary.imageUrl,
      vehicleInfo.imageUrl,
      vehicleDetails.imageUrl,
      item?.imageUrl
    ),
    modelImage: pickFirst(
      vehicle.modelImage,
      vehicleSummary.modelImage,
      vehicleInfo.modelImage,
      vehicleDetails.modelImage,
      item?.modelImage
    ),
  };
}

function summarizeCartVehicleDebug(item) {
  const v = resolveCartVehicle(item);
  return {
    id: item?.id ?? null,
    quantity: item?.quantity ?? 1,
    vehicleRegistrationNumber:
      item?.vehicleRegistrationNumber ??
      item?.registrationNumber ??
      item?.vehicle?.registrationNumber ??
      null,
    catalogBrand: item?.catalogBrand ?? null,
    catalogModel: item?.catalogModel ?? null,
    catalogType: item?.catalogType ?? null,
    catalogColor: item?.catalogColor ?? null,
    vehicle: item?.vehicle
      ? {
          registrationNumber: item.vehicle.registrationNumber ?? null,
          brand: item.vehicle.brand ?? null,
          model: item.vehicle.model ?? null,
          type: item.vehicle.type ?? null,
          seatingCapacity: item.vehicle.seatingCapacity ?? null,
          fuelType: item.vehicle.fuelType ?? null,
          color: item.vehicle.color ?? null,
          yearOfPurchase: item.vehicle.yearOfPurchase ?? null,
        }
      : null,
    resolved: {
      registrationNumber: v.registrationNumber ?? null,
      brand: v.brand ?? null,
      model: v.model ?? null,
      type: v.type ?? null,
      seatingCapacity: v.seatingCapacity ?? null,
      color: v.color ?? null,
      yearOfPurchase: v.yearOfPurchase ?? null,
    },
  };
}

const CATALOG_VEHICLE_META_KEY = "wr_catalog_vehicle_meta_v1";

function readCatalogVehicleMetaCache() {
  try {
    const raw = window.localStorage.getItem(CATALOG_VEHICLE_META_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

function getCatalogVehicleMeta(item, cache) {
  const key = String(
    item?.vehicle?.registrationNumber ||
      item?.vehicleSummary?.registrationNumber ||
      item?.vehicleInfo?.registrationNumber ||
      item?.vehicleDetails?.registrationNumber ||
      item?.registrationNumber ||
      item?.vehicleRegistrationNumber ||
      ""
  ).trim();

  if (!key) return null;
  return cache?.[key] || null;
}

function hydrateCartItem(item, cache) {
  const meta = getCatalogVehicleMeta(item, cache);
  if (!meta) return item;

  const nextVehicle = {
    ...(item?.vehicle || {}),
    registrationNumber:
      item?.vehicle?.registrationNumber ||
      meta.registrationNumber ||
      item?.registrationNumber ||
      item?.vehicleRegistrationNumber,
    brand: item?.vehicle?.brand || meta.brand || item?.brand,
    model: item?.vehicle?.model || meta.model || item?.model,
    type: item?.vehicle?.type || meta.type || item?.type,
    seatingCapacity:
      item?.vehicle?.seatingCapacity ??
      item?.vehicle?.seatCount ??
      item?.vehicle?.seats ??
      item?.seatingCapacity ??
      meta.seatingCapacity ??
      null,
    fuelType: item?.vehicle?.fuelType || item?.fuelType || meta.fuelType || "",
    color: item?.vehicle?.color || item?.color || meta.color || "",
    yearOfPurchase:
      item?.vehicle?.yearOfPurchase ??
      item?.yearOfPurchase ??
      item?.year ??
      item?.manufacturingYear ??
      item?.modelYear ??
      meta.yearOfPurchase ??
      null,
    imageUrl: item?.vehicle?.imageUrl || item?.imageUrl || meta.imageUrl || null,
    modelImage: item?.vehicle?.modelImage || item?.modelImage || meta.modelImage || null,
  };

  return {
    ...meta,
    ...item,
    brand: item?.brand || meta.brand || "",
    model: item?.model || meta.model || "",
    type: item?.type || meta.type || "",
    seatingCapacity: item?.seatingCapacity ?? meta.seatingCapacity ?? null,
    fuelType: item?.fuelType || meta.fuelType || "",
    color: item?.color || meta.color || "",
    yearOfPurchase: item?.yearOfPurchase ?? meta.yearOfPurchase ?? null,
    imageUrl: item?.imageUrl || meta.imageUrl || null,
    modelImage: item?.modelImage || meta.modelImage || null,
    vehicleRegistrationNumber:
      item?.vehicleRegistrationNumber || item?.registrationNumber || meta.registrationNumber,
    registrationNumber:
      item?.registrationNumber || item?.vehicleRegistrationNumber || meta.registrationNumber,
    vehicle: nextVehicle,
  };
}

/* ───────── Formatting ───────── */
const fmtINR = (n) =>
  new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR" }).format(
    Number.isFinite(n) ? n : 0
  );

function bookingOfferCopy({ bookingAmount, originalBookingAmount, pendingCash, discountPercent = 0 }) {
  const fullyWaived = bookingAmount <= 0;
  const discountValue = Math.max(0, Number(originalBookingAmount || 0) - Number(bookingAmount || 0));
  const hasOffer = discountValue > 0.009;
  return {
    badge: fullyWaived ? "BOOKING DISCOUNT" : hasOffer ? `${discountPercent}% OFF BOOKING` : "BOOKING SUMMARY",
    eyebrow: "Booking payment",
    headline: fullyWaived
      ? "Booking amount discounted to ₹0"
      : hasOffer
      ? `Revised booking amount ${fmtINR(bookingAmount)}`
      : `Booking amount to pay now ${fmtINR(bookingAmount)}`,
    summary: fullyWaived
      ? `No booking amount to pay now. Pay ${fmtINR(pendingCash)} after ride.`
      : hasOffer
      ? `Total fare includes the booking amount. Your offer reduces the overall payable amount by ${fmtINR(discountValue)}.`
      : `Total fare already includes the booking amount. Pay ${fmtINR(pendingCash)} after ride.`,
    bookNow: fullyWaived ? "₹0" : fmtINR(bookingAmount),
    payLater: fmtINR(pendingCash),
    beforeOffer: hasOffer ? fmtINR(originalBookingAmount) : null,
    discountValue: hasOffer ? fmtINR(discountValue) : null,
    hasOffer,
    fullyWaived,
  };
}

const n = (x) => (Number.isFinite(Number(x)) ? Number(x) : 0);
const qtyOf = (item) => Math.max(1, Number(item?.quantity ?? 1));
const normalizedCartQuantity = (quantity) => Math.max(1, Math.floor(Number(quantity) || 1));
const ceilMoney = (value) => Math.ceil(Math.max(0, n(value)));
const unitBookingAmountOf = (item) =>
  ceilMoney(n(item?.commission) + n(item?.apiCharges) + n(item?.gst));
let currentScheduleBookingDiscountPercent = 0;
const scheduledBookingPricingModeOf = (item) =>
  String(item?.scheduledBookingPricingMode || item?.pricingMode || "").trim().toUpperCase() === "DISCOUNT_REDUCES_TOTAL"
    ? "DISCOUNT_REDUCES_TOTAL"
    : "LEGACY_NO_DISCOUNT";
const bookingPaymentSummaryOf = (item) =>
  summarizeScheduledBookingAmounts({
    totalFare: totalFareOf(item),
    bookingAmount: unitBookingAmountOf(item) * qtyOf(item),
    discountPercent: currentScheduleBookingDiscountPercent,
    pricingMode: scheduledBookingPricingModeOf(item),
  });
const bookingAmountOf = (item) => bookingPaymentSummaryOf(item).effectiveBookingAmount;
const unitFareOf = (item) => {
  const total = n(item?.fare);
  const toll = n(item?.toll);
  const displayToll = n(item?.displayToll ?? item?.display_toll ?? toll);
  return ceilMoney(total - toll + displayToll);
};
const totalFareOf = (item) => unitFareOf(item) * qtyOf(item);
const pendingCashOf = (item) => bookingPaymentSummaryOf(item).pendingAfterRide;

function formatDateWithSuffix(dateStr) {
  if (!dateStr) return "N/A";
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return "N/A";
  const day = d.getDate();
  const month = d.toLocaleString("en-GB", { month: "long" });
  const year = d.getFullYear();
  const suf =
    day >= 11 && day <= 13
      ? "th"
      : ["th", "st", "nd", "rd"][Math.min(day % 10, 4)] || "th";
  return `${day}${suf} ${month} ${year}`;
}
function formatTime12(timeStr) {
  if (!timeStr) return "N/A";
  const [hh, mm = "0"] = String(timeStr).split(":");
  const h = parseInt(hh, 10);
  if (isNaN(h)) return "N/A";
  const ampm = h >= 12 ? "PM" : "AM";
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:${String(parseInt(mm, 10)).padStart(2, "0")} ${ampm}`;
}

/* ───────── Fare breakup helpers ───────── */
const Chevron = ({ expanded }) => (
  <span
    className={`inline-block text-[10px] transition-transform duration-150 ${
      expanded ? "rotate-90" : "rotate-0"
    }`}
    aria-hidden="true"
  >
    ▸
  </span>
);

/* ───────── Plan grouping helpers (mirror app cart) ───────── */
function normStr(s) {
  return (s ?? "").toString().trim();
}

function makeTripKey(it) {
  const stops = Array.isArray(it?.destinations)
    ? it.destinations.map((s) => normStr(s)).filter(Boolean)
    : [];
  return JSON.stringify({
    pickupLocation: normStr(it?.pickupLocation),
    destinations: stops,
    destination: normStr(it?.destination || it?.finalDestination),
    pickupDate: normStr(it?.pickupDate),
    pickupTime: normStr(it?.pickupTime),
    dropDate: normStr(it?.dropDate),
    dropTime: normStr(it?.dropTime),
  });
}

function groupCart(items) {
  const map = new Map();
  for (const it of items) {
    const key = makeTripKey(it);
    if (!map.has(key)) {
      map.set(key, {
        key,
        pickupLocation: normStr(it?.pickupLocation),
        destination: normStr(it?.destination || it?.finalDestination),
        stops: Array.isArray(it?.destinations)
          ? it.destinations.map((s) => normStr(s)).filter(Boolean)
          : [],
        pickupDate: it?.pickupDate || null,
        pickupTime: it?.pickupTime || null,
        dropDate: it?.dropDate || null,
        dropTime: it?.dropTime || null,
        vehicles: [],
      });
    }
    map.get(key).vehicles.push(it);
  }
  return Array.from(map.values()).sort((a, b) => (a.key < b.key ? -1 : 1));
}

const sumGroupFare = (g) =>
  g.vehicles.reduce((acc, it) => acc + totalFareOf(it), 0);
const sumGroupOriginalBookingAmount = (g) =>
  g.vehicles.reduce((acc, it) => acc + unitBookingAmountOf(it) * qtyOf(it), 0);
const sumGroupBookingAmount = (g) =>
  g.vehicles.reduce((acc, it) => acc + bookingAmountOf(it), 0);
const sumGroupPendingCash = (g) =>
  g.vehicles.reduce((acc, it) => acc + pendingCashOf(it), 0);

/* ───────── Expiry helpers ───────── */
function parsePlanStart(pickupDate, pickupTime) {
  if (!pickupDate) return null;

  const parts = String(pickupDate).split("-");
  if (parts.length === 3) {
    const [y, m, d] = parts.map((p) => Number(p));
    if (Number.isFinite(y) && Number.isFinite(m) && Number.isFinite(d)) {
      let hh = 0;
      let mm = 0;
      if (pickupTime) {
        const tParts = String(pickupTime).split(":");
        hh = Number(tParts[0]) || 0;
        mm = Number(tParts[1]) || 0;
      }
      const dt = new Date(y, m - 1, d, hh, mm, 0, 0);
      return Number.isNaN(dt.getTime()) ? null : dt;
    }
  }

  const fallback = new Date(pickupDate);
  if (Number.isNaN(fallback.getTime())) return null;
  return fallback;
}

function isPlanExpired(g) {
  const start = parsePlanStart(g.pickupDate, g.pickupTime);
  if (!start) return false;
  const now = new Date();
  return start.getTime() < now.getTime();
}

/* ───────── Vehicle age helper (for cart card) ───────── */
function getVehicleAgeLabel(v) {
  const year = Number(
    v.yearOfPurchase ?? v.year ?? v.manufacturingYear ?? v.modelYear
  );
  if (!Number.isFinite(year)) return "N/A";
  const currentYear = new Date().getFullYear();
  const age = Math.max(currentYear - year, 0);
  if (age <= 0) return `${year}`;
  if (age === 1) return "1 year old";
  return `${age} years old`;
}

function getVehicleAgeChipLabel(v) {
  const label = getVehicleAgeLabel(v);
  return label === "N/A" ? "" : label;
}

function MobileStageCard({ icon, label, location, dateTime }) {
  return (
    <div className="flex min-w-0 flex-1 flex-col rounded-2xl border border-slate-200 bg-slate-50/80 p-3">
      <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.08em] text-slate-500">
        <Icon path={icon} size={0.7} />
        <span>{label}</span>
      </div>
      <div className="mt-2 line-clamp-2 text-[13px] font-semibold leading-snug text-slate-900">
        {location || "—"}
      </div>
      <div className="mt-2 rounded-xl border border-slate-200 bg-white px-3 py-2">
        <div className="flex items-center gap-1.5 text-[11px] text-slate-600">
          <Icon path={mdiCalendarMonthOutline} size={0.62} />
          <span className="truncate">{dateTime.date || "N/A"}</span>
        </div>
        <div className="mt-1 flex items-center gap-1.5 text-[11px] text-slate-600">
          <Icon path={mdiClockOutline} size={0.62} />
          <span className="truncate">{dateTime.time || "N/A"}</span>
        </div>
      </div>
    </div>
  );
}

function cartVehicleTitle(item) {
  const v = resolveCartVehicle(item);
  return `${v.brand || ""} ${v.model || ""}`.trim() || "Vehicle";
}

/* ───────── Compact spec row for cart vehicle cards ───────── */
const SpecRow = ({ label, children }) => (
  <div className="flex items-center justify-between rounded-lg border border-slate-200 bg-slate-50 px-3.5 py-2 text-[13px]">
    <span className="text-[11px] uppercase tracking-[0.06em] text-slate-500">
      {label}
    </span>
    <span className="whitespace-nowrap font-semibold text-slate-900">
      {children}
    </span>
  </div>
);

const QuantityAdjuster = ({ quantity, busy, onDecrease, onIncrease }) => (
  <div className="rounded-xl border border-slate-200 bg-white px-3.5 py-3">
    <div className="text-[13px] text-slate-600">{busy ? "Updating quantity..." : "Adjust quantity"}</div>
    <div className="mt-3 flex items-center justify-end gap-3">
      <button
        type="button"
        onClick={onDecrease}
        disabled={busy || quantity <= 1}
        className="inline-flex h-9 w-9 items-center justify-center rounded-xl border border-slate-200 bg-white text-lg font-semibold text-slate-900 disabled:cursor-not-allowed disabled:opacity-45"
      >
        -
      </button>
      <div className="min-w-[54px] rounded-xl border border-slate-200 bg-slate-100 px-3 py-2 text-center text-[14px] font-bold text-slate-900 tabular-nums">
        {quantity}
      </div>
      <button
        type="button"
        onClick={onIncrease}
        disabled={busy}
        className="inline-flex h-9 w-9 items-center justify-center rounded-xl border border-slate-200 bg-white text-lg font-semibold text-slate-900 disabled:cursor-not-allowed disabled:opacity-45"
      >
        +
      </button>
    </div>
  </div>
);

const PaymentOverviewCard = ({
  totalFare,
  totalBookingAmount,
  totalPendingCash,
  totalOriginalBookingAmount,
  discountPercent = 0,
}) => {
  const offer = bookingOfferCopy({
    bookingAmount: totalBookingAmount,
    originalBookingAmount: totalOriginalBookingAmount,
    pendingCash: totalPendingCash,
    discountPercent,
  });
  const rows = [
    { label: "Total fare", value: totalFare },
    { label: "Booking amount", value: totalOriginalBookingAmount },
    ...(offer.hasOffer ? [{ label: "Booking amount discount", value: `- ${offer.discountValue}` }] : []),
    { label: "Booking amount to pay now", value: offer.bookNow },
    { label: "Pay after ride", value: offer.payLater },
  ];

  return (
    <section className="mt-6 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm sm:rounded-2xl">
      <div className="border-b border-slate-200 px-4 py-3 sm:px-5 sm:py-4">
        <h2 className="text-[15px] font-extrabold text-slate-900 sm:text-[17px]">Payment Overview</h2>
        <p className="mt-1 text-[12px] leading-5 text-slate-500">
          Total fare includes the booking amount. Any booking discount reduces the overall payable fare.
        </p>
      </div>
      <div className="px-4 py-1.5 sm:px-5 sm:py-2">
        {rows.map((row, index) => (
          <div
            key={row.label}
            className={`flex items-start justify-between gap-3 py-3 sm:py-4 ${
              index < rows.length - 1 ? "border-b border-slate-100" : ""
            }`}
          >
            <div className="min-w-0 pr-2 text-[12px] leading-5 text-slate-700 sm:max-w-[760px] sm:text-[14px]">
              {row.label}
            </div>
            <div className="shrink-0 text-right text-[16px] font-extrabold text-slate-900 tabular-nums sm:text-[18px]">
              {typeof row.value === "number" ? fmtINR(row.value) : row.value}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};

/* ───────── Page ───────── */
export default function CartPage() {
  const { user, role, isAuthenticated, booted } = useUser();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [scheduleBookingDiscountPercent, setScheduleBookingDiscountPercent] = useState(0);
  const imageIndex = useMemo(() => makeImageIndex(carDetails), []);

  const [collapsedPlanKeys, setCollapsedPlanKeys] = useState([]);
  const [updatingItemId, setUpdatingItemId] = useState(null);

  const [planToRemove, setPlanToRemove] = useState(null);
  const [planRemoveBusy, setPlanRemoveBusy] = useState(false);
  const [mobileStopsModal, setMobileStopsModal] = useState({
    open: false,
    stops: [],
  });
  useEffect(() => {
    let alive = true;
    getSearchResultsConfig()
      .then((config) => {
        if (alive) setScheduleBookingDiscountPercent(Number(config?.scheduleBookingDiscountPercent) || 0);
      })
      .catch(() => {
        if (alive) setScheduleBookingDiscountPercent(0);
      });
    return () => {
      alive = false;
    };
  }, []);

  currentScheduleBookingDiscountPercent = scheduleBookingDiscountPercent;

  // ✅ Load cart using new identity (userId preferred; fallback phone/email via cartApi)
  useEffect(() => {
    if (!booted) return;

    // Only USER can access cart
    if (!isAuthenticated || role !== ROLES.USER) {
      navigate("/signin-required", { replace: true, state: { from: "/cart" } });
      console.log("API =", process.env.REACT_APP_API_BASE_URL);
      return;
    }

    (async () => {
      try {
        const cart = await getCartForCurrentUser();
        console.log("[WEB][Cart] raw cart response", {
          itemCount: Array.isArray(cart?.items) ? cart.items.length : 0,
          sample: Array.isArray(cart?.items) && cart.items.length ? cart.items[0] : null,
        });
        const cache = readCatalogVehicleMetaCache();
        console.log("[WEB][Cart] catalog meta cache", {
          keys: Object.keys(cache || {}),
          sample:
            Object.keys(cache || {}).length > 0
              ? cache[Object.keys(cache)[0]]
              : null,
        });
        const nextItems = Array.isArray(cart?.items)
          ? cart.items.map((item) => hydrateCartItem(item, cache))
          : [];
        console.log("[WEB][Cart] hydrated cart items", nextItems.map(summarizeCartVehicleDebug));
        const unresolved = nextItems
          .map((item) => summarizeCartVehicleDebug(item))
          .filter((item) => !item.resolved.brand || !item.resolved.model);
        if (unresolved.length) {
          console.warn("[WEB][Cart] unresolved catalog vehicle rows", unresolved);
        }
        setItems(nextItems);
      } catch (e) {
        console.error("Failed to load cart", e);

        // If unified cartApi cannot resolve identity, redirect to signin
        if (e?.code === "AUTH_REQUIRED") {
          navigate("/signin-required", { replace: true, state: { from: "/cart" } });
          return;
        }

        setItems([]);
      }
    })();
  }, [booted, isAuthenticated, role, navigate, user]);

  const groups = useMemo(() => groupCart(items), [items]);

  useEffect(() => {
    if (!items.length) return;
    console.log("[WEB][Cart] render items snapshot", items.map(summarizeCartVehicleDebug));
  }, [items]);

  const totalFare = useMemo(
    () =>
      groups
        .filter((g) => !isPlanExpired(g))
        .reduce((acc, g) => acc + sumGroupFare(g), 0),
    [groups]
  );

  const totalBookingAmount = useMemo(
    () =>
      groups
        .filter((g) => !isPlanExpired(g))
        .reduce((acc, g) => acc + sumGroupBookingAmount(g), 0),
    [groups]
  );

  const totalOriginalBookingAmount = useMemo(
    () =>
      groups
        .filter((g) => !isPlanExpired(g))
        .reduce((acc, g) => acc + sumGroupOriginalBookingAmount(g), 0),
    [groups]
  );

  const totalPendingCash = useMemo(
    () =>
      groups
        .filter((g) => !isPlanExpired(g))
        .reduce((acc, g) => acc + sumGroupPendingCash(g), 0),
    [groups]
  );

  const activeItems = useMemo(
    () => groups.filter((g) => !isPlanExpired(g)).flatMap((g) => g.vehicles),
    [groups]
  );

  const hasNonExpiredGroups = useMemo(
    () => groups.some((g) => !isPlanExpired(g)),
    [groups]
  );

  const handleRemoveVehicle = async (cartItemId) => {
    const idNum = Number(cartItemId);
    if (!Number.isFinite(idNum)) return;
    if (!isAuthenticated || role !== ROLES.USER) {
      navigate("/signin-required", { replace: false, state: { from: "/cart" } });
      return;
    }

    try {
      await removeCartItemByIdForCurrentUser(idNum);
      setItems((prev) => prev.filter((it) => Number(it?.id) !== idNum));
    } catch (e) {
      console.error("Remove failed", e);
      if (e?.code === "AUTH_REQUIRED") {
        navigate("/signin-required", { replace: false, state: { from: "/cart" } });
      }
    }
  };

  const updateItemQuantity = async (item, quantity) => {
    const idNum = Number(item?.id);
    if (!Number.isFinite(idNum)) return;

    setUpdatingItemId(idNum);
    try {
      const nextQuantity = normalizedCartQuantity(quantity);
      await updateCartItemQuantityForCurrentUser(idNum, nextQuantity);
      setItems((prev) =>
        prev.map((entry) =>
          Number(entry?.id) === idNum ? { ...entry, quantity: nextQuantity } : entry
        )
      );
    } catch (e) {
      console.error("Update quantity failed", e);
    } finally {
      setUpdatingItemId(null);
    }
  };

  const confirmRemovePlan = (g) => {
    setPlanToRemove(g);
    setPlanRemoveBusy(false);
  };

  const actuallyRemovePlan = async () => {
    if (!planToRemove) return;

    if (!isAuthenticated || role !== ROLES.USER) {
      navigate("/signin-required", { replace: false, state: { from: "/cart" } });
      return;
    }

    setPlanRemoveBusy(true);
    try {
      const cartItemIds = planToRemove.vehicles
        .map((v) => Number(v?.id))
        .filter((id) => Number.isFinite(id));

      await Promise.all(cartItemIds.map((id) => removeCartItemByIdForCurrentUser(id)));

      setItems((prev) => prev.filter((it) => !cartItemIds.includes(Number(it?.id))));
      setPlanToRemove(null);
    } catch (e) {
      console.error("Remove plan failed", e);
      if (e?.code === "AUTH_REQUIRED") {
        navigate("/signin-required", { replace: false, state: { from: "/cart" } });
        return;
      }
      setPlanRemoveBusy(false);
    }
  };

  // plan card click: open/close vehicles when clicking anywhere except buttons/links
  const handlePlanCardClick = (e, key) => {
    const target = e.target;
    if (target.closest("button, a, [data-no-toggle='true']")) {
      return;
    }
    setCollapsedPlanKeys((prev) =>
      prev.includes(key) ? prev.filter((entry) => entry !== key) : [...prev, key]
    );
  };

  return (
    <div className="min-h-screen bg-white text-slate-900">
      <Header />
      <main className="mx-auto max-w-[1440px] px-4 py-5 sm:px-6 sm:py-8 xl:px-10 xl:py-10">
        <div className="flex items-baseline justify-between gap-4">
          <h1 className="text-[28px] font-bold tracking-tight sm:text-3xl">My Cart</h1>
        </div>

        {!groups.length ? (
          <div className="mt-6 rounded-xl border border-slate-200 bg-white p-6 text-center shadow-sm sm:mt-10 sm:rounded-2xl sm:p-10">
            <div className="text-5xl">🛒</div>
            <div className="mt-2 text-slate-700">Your cart is empty</div>
            <div className="text-sm text-slate-500">
              Looks like you haven’t added any vehicles yet.
            </div>
            <Link
              className="mt-4 inline-flex rounded-2xl bg-black px-5 py-2.5 text-sm font-semibold text-white"
              to="/discover"
            >
              Browse vehicles
            </Link>
          </div>
        ) : (
          <>
            {/* Trip plans list */}
            <div className="mt-6 space-y-6">
              {groups.map((g, idx) => {
                const expired = isPlanExpired(g);
                const planTotal = sumGroupFare(g);
                const planOriginalBookingAmount = sumGroupOriginalBookingAmount(g);
                const planBookingAmount = sumGroupBookingAmount(g);
                const planPendingCash = sumGroupPendingCash(g);
                const planDiscountSummary = {
                  originalBookingAmount: planOriginalBookingAmount,
                  effectiveBookingAmount: planBookingAmount,
                  pendingAfterRide: planPendingCash,
                  discountAmount: Math.max(0, planOriginalBookingAmount - planBookingAmount),
                  discountPercent:
                    planOriginalBookingAmount > 0 && planBookingAmount < planOriginalBookingAmount
                      ? scheduleBookingDiscountPercent
                      : 0,
                  fullyWaived: planBookingAmount <= 0,
                };
                const totalPlanCars = g.vehicles.reduce((acc, it) => acc + qtyOf(it), 0);
                const pDate = formatDateWithSuffix(g.pickupDate);
                const dDate = formatDateWithSuffix(g.dropDate);
                const pTime = formatTime12(g.pickupTime);
                const dTime = formatTime12(g.dropTime);

                const pickupDisplay =
                  [pDate, pTime].filter((x) => x && x !== "N/A").join(" • ") ||
                  "N/A";
                const dropDisplay =
                  [dDate, dTime].filter((x) => x && x !== "N/A").join(" • ") ||
                  "N/A";

                const stops = g.stops;
                const expanded = !collapsedPlanKeys.includes(g.key);

                const vehiclesSummary = Array.from(
                  g.vehicles.reduce((map, it, i) => {
                    const v = resolveCartVehicle(it);
                    const name = cartVehicleTitle(it) || "—";
                    const reg = v.registrationNumber || "";
                    const summaryKey = reg || `${name}-${i}`;
                    const current = map.get(summaryKey);
                    if (current) {
                      current.quantity += qtyOf(it);
                    } else {
                      map.set(summaryKey, {
                        name,
                        reg,
                        quantity: qtyOf(it),
                        key: summaryKey,
                      });
                    }
                    return map;
                  }, new Map()).values()
                );

                const routeNodes = [
                  {
                    label: "Pickup",
                    text: g.pickupLocation || "—",
                    dotClass: "bg-slate-400",
                  },
                  ...stops.map((stop, i) => ({
                    label: `Stop ${i + 1}`,
                    text: stop,
                    dotClass: "bg-slate-400",
                  })),
                  {
                    label: "Drop",
                    text: g.destination || "—",
                    dotClass: "bg-slate-400",
                  },
                ];

                return (
                  <article
                    key={g.key}
                    className={[
                      "rounded-xl border bg-white shadow-sm transition sm:rounded-2xl",
                      expired
                        ? "border-red-200 bg-red-50/40"
                        : "border-slate-200 hover:shadow-md",
                    ].join(" ")}
                    onClick={(e) => handlePlanCardClick(e, g.key)}
                  >
                    {/* Plan header & sections */}
                    <div className="px-4 pb-3 pt-4 md:px-6 md:pt-5">
                      <div className="flex flex-wrap items-center gap-3">
                        <span className="inline-flex items-center rounded-md bg-slate-900 px-3 py-1 text-xs font-semibold text-white">
                          Plan {idx + 1}
                        </span>

                        <span className="inline-flex items-center rounded-md bg-slate-100 px-3 py-1 text-xs text-slate-700">
                          {totalPlanCars} {totalPlanCars === 1 ? "car" : "cars"}
                        </span>

                        <button
                          type="button"
                          data-no-toggle="true"
                          onClick={(e) => {
                            e.stopPropagation();
                            setCollapsedPlanKeys((prev) =>
                              prev.includes(g.key)
                                ? prev.filter((entry) => entry !== g.key)
                                : [...prev, g.key]
                            );
                          }}
                          className="hidden items-center gap-1.5 rounded-md border border-slate-200 px-2.5 py-1 text-[11px] font-medium text-slate-600 hover:bg-slate-50 sm:inline-flex"
                        >
                          <span>
                            {expanded ? "Hide vehicles" : "View vehicles"}
                          </span>
                          <Chevron expanded={expanded} />
                        </button>

                        {expired && (
                          <span className="inline-flex items-center rounded-md border border-red-200 bg-red-50 px-3 py-1 text-[11px] font-medium text-red-700">
                            Trip date passed
                          </span>
                        )}

                        <div className="ml-auto flex items-center gap-3">
                          <span className="hidden flex-col items-end text-xs text-slate-500 sm:inline-flex">
                            <span className="uppercase tracking-wide">
                              Trip total
                            </span>
                            <span className="text-sm font-semibold text-slate-900">
                              {fmtINR(planTotal)}
                            </span>
                            {!expired && (
                              <span className="mt-1 text-right text-[11px] text-slate-500">
                                {planDiscountSummary.fullyWaived
                                  ? "Pay after ride"
                                  : `Pay now ${fmtINR(planBookingAmount)}`}
                              </span>
                            )}
                          </span>

                          <button
                            type="button"
                            data-no-toggle="true"
                            onClick={(e) => {
                              e.stopPropagation();
                              confirmRemovePlan(g);
                            }}
                            className="inline-flex items-center justify-center rounded-md border border-red-300 px-3 py-2 text-xs font-semibold text-red-700 hover:bg-red-50 active:scale-[0.99]"
                          >
                            Remove plan
                          </button>
                        </div>
                      </div>

                      <div className="mt-4 sm:hidden">
                        <div className="grid grid-cols-2 gap-3">
                          <MobileStageCard
                            icon={mdiMapMarkerOutline}
                            label="Pickup"
                            location={g.pickupLocation || "—"}
                            dateTime={{ date: pDate, time: pTime }}
                          />
                          <MobileStageCard
                            icon={mdiFlagOutline}
                            label="Drop"
                            location={g.destination || "—"}
                            dateTime={{ date: dDate, time: dTime }}
                          />
                        </div>

                        <div className="mt-3 flex items-start justify-between rounded-2xl border border-slate-200 bg-white px-4 py-3">
                          <div className="text-[11px] font-semibold uppercase tracking-[0.08em] text-slate-500">
                            Trip total
                          </div>
                            <div className="text-right">
                              <div className="text-[15px] font-extrabold text-slate-900">
                                {fmtINR(planTotal)}
                              </div>
                              {!expired ? (
                                <div className="mt-0.5 text-[11px] text-slate-500">
                                  {planDiscountSummary.fullyWaived
                                    ? "Pay after ride"
                                    : `Pay now ${fmtINR(planBookingAmount)}`}
                                </div>
                              ) : null}
                            </div>
                        </div>

                        {!!stops.length && (
                          <button
                            type="button"
                            data-no-toggle="true"
                            onClick={(e) => {
                              e.stopPropagation();
                              setMobileStopsModal({ open: true, stops });
                            }}
                            className="mt-3 flex w-full items-center justify-between rounded-2xl border border-slate-200 bg-slate-50/80 px-4 py-3 text-left"
                          >
                            <div className="flex items-center gap-2 text-slate-700">
                              <Icon path={mdiMapMarkerPath} size={0.72} />
                              <span className="text-sm font-medium">Stops</span>
                            </div>
                            <span className="rounded-full border border-slate-200 bg-white px-2.5 py-1 text-[12px] font-semibold text-slate-700">
                              {stops.length}
                            </span>
                          </button>
                        )}
                      </div>

                      <div className="mt-4 hidden gap-4 xl:grid-cols-[minmax(0,2.2fr)_minmax(0,1.6fr)_minmax(0,1.8fr)] xl:gap-6 sm:grid">
                        <section className="space-y-3">
                          <div className="text-[11px] font-semibold uppercase tracking-[0.08em] text-slate-500">
                            Route
                          </div>
                          <div className="space-y-3">
                            {routeNodes.map((node, idxNode) => (
                              <div
                                key={`${node.label}-${idxNode}`}
                                className="flex items-start gap-3"
                              >
                                <div className="flex flex-col items-center">
                                  <span
                                    className={`h-2 w-2 rounded-full ${node.dotClass}`}
                                  />
                                  {idxNode < routeNodes.length - 1 && (
                                    <span className="mt-1 w-px flex-1 bg-slate-200" />
                                  )}
                                </div>
                                <div className="min-w-0">
                                  <div className="text-[11px] font-medium text-slate-500">
                                    {node.label}
                                  </div>
                                  <div className="line-clamp-2 text-[14px] font-medium leading-snug text-slate-900">
                                    {node.text}
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </section>

                        <section className="space-y-3">
                          <div className="text-[11px] font-semibold uppercase tracking-[0.08em] text-slate-500">
                            Schedule
                          </div>
                          <div className="space-y-3">
                            <div className="flex flex-col gap-1.5 rounded-xl border border-slate-200 bg-slate-50 px-3.5 py-3">
                              <div className="text-[11px] font-medium uppercase text-slate-500">
                                Pickup
                              </div>
                              <div className="text-[13px] font-medium text-slate-900">
                                {pickupDisplay}
                              </div>
                            </div>
                            <div className="flex flex-col gap-1.5 rounded-xl border border-slate-200 bg-slate-50 px-3.5 py-3">
                              <div className="text-[11px] font-medium uppercase text-slate-500">
                                Drop
                              </div>
                              <div className="text-[13px] font-medium text-slate-900">
                                {dropDisplay}
                              </div>
                            </div>
                          </div>
                        </section>

                        <section className="space-y-3">
                          <div className="text-[11px] font-semibold uppercase tracking-[0.08em] text-slate-500">
                            Vehicles in plan
                          </div>
                          <div className="mt-1 max-h-32 overflow-y-auto rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 custom-scrollbar">
                            {vehiclesSummary.length === 0 ? (
                              <div className="text-[12px] text-slate-500">
                                No vehicles in this plan.
                              </div>
                            ) : (
                              <div className="space-y-1.5">
                                {vehiclesSummary.map(({ name, reg, quantity, key }) => (
                                  <div
                                    key={key}
                                    className="flex items-center gap-2 text-[12px] text-slate-800"
                                  >
                                    <span className="h-1.5 w-1.5 rounded-full bg-slate-400" />
                                    <span className="truncate font-medium">
                                      {name}
                                    </span>
                                    <span className="rounded-full bg-white px-2 py-0.5 text-[11px] font-medium text-slate-600">
                                      x{quantity}
                                    </span>
                                    {reg && (
                                      <span className="ml-auto text-[11px] tabular-nums text-slate-500">
                                        {reg}
                                      </span>
                                    )}
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        </section>
                      </div>

                      {!expired && g.vehicles.length > 1 ? (
                        <div className="mt-4 hidden rounded-2xl border border-slate-200 bg-slate-50/70 px-4 py-4 sm:block">
                          <div className="text-[11px] font-semibold uppercase tracking-[0.08em] text-slate-500">
                            Plan level calculation
                          </div>
                          <div className="mt-3 grid gap-3 md:grid-cols-3">
                            <div className="rounded-xl border border-slate-200 bg-white px-4 py-3">
                              <div className="text-[11px] uppercase tracking-[0.08em] text-slate-500">
                                Total plan fare
                              </div>
                              <div className="mt-2 text-[20px] font-extrabold text-slate-900 tabular-nums">
                                {fmtINR(planTotal)}
                              </div>
                            </div>
                            <div className="rounded-xl border border-slate-200 bg-white px-4 py-3">
                              <div className="text-[11px] uppercase tracking-[0.08em] text-slate-500">
                                Booking amount now
                              </div>
                              <div className="mt-2 text-[20px] font-extrabold text-slate-900 tabular-nums">
                                {planDiscountSummary.fullyWaived ? "₹0" : fmtINR(planBookingAmount)}
                              </div>
                              <div className="mt-1 text-[11px] text-slate-500">
                                {planDiscountSummary.discountPercent > 0
                                  ? planDiscountSummary.fullyWaived
                                    ? `Discount applied on ${fmtINR(planDiscountSummary.originalBookingAmount)} booking amount`
                                    : `${fmtINR(planDiscountSummary.originalBookingAmount - planBookingAmount)} offer applied`
                                  : "Due today"}
                              </div>
                            </div>
                            <div className="rounded-xl border border-slate-200 bg-white px-4 py-3">
                              <div className="text-[11px] uppercase tracking-[0.08em] text-slate-500">
                                Pay after ride
                              </div>
                              <div className="mt-2 text-[20px] font-extrabold text-slate-900 tabular-nums">
                                {fmtINR(planPendingCash)}
                              </div>
                            </div>
                          </div>
                        </div>
                      ) : null}
                    </div>

                    {/* Vehicles list */}
                    {expanded && (
                      <div
                        className="space-y-4 border-t border-slate-200 bg-slate-50/60 px-4 py-4 md:px-6"
                        data-no-toggle="true"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {g.vehicles.map((it, idxV) => {
                          const v = resolveCartVehicle(it);
                          const img = getImage(imageIndex, v.brand, v.model, v.color);
                          const title = `${v.brand || "—"} ${v.model || ""}`.trim();
                          const ageLabel = getVehicleAgeChipLabel(v);
                          const quantity = qtyOf(it);
                          const unitFare = unitFareOf(it);
                          const bookingSummary = bookingPaymentSummaryOf(it);
                          const quantityBusy = updatingItemId === Number(it?.id);

                          return (
                            <article
                              key={`${v?.registrationNumber ?? "no-reg"}-${idxV}`}
                              className="group rounded-xl border border-slate-200 bg-white shadow-sm transition hover:shadow-md sm:rounded-2xl"
                            >
                              <div className="flex flex-col lg:flex-row lg:items-stretch">
                                <div className="relative min-h-[180px] overflow-hidden rounded-t-xl bg-white sm:min-h-[220px] sm:rounded-t-2xl lg:min-h-0 lg:w-[392px] lg:rounded-l-2xl lg:rounded-tr-none">
                                  <VehicleImage
                                    imageUrl={v.imageUrl || img}
                                    modelImage={v.modelImage || null}
                                    brand={v.brand}
                                    model={v.model}
                                    color={v.color}
                                    type={v.type}
                                    alt={`${v.brand} ${v.model} ${v.color || ""}`.trim()}
                                    wrapperClassName="absolute inset-0 flex items-center justify-start"
                                    className="h-[96%] w-full scale-[1.04] object-contain object-left"
                                    allowSkeleton
                                  />
                                </div>

                                <div className="flex-1 p-4 md:p-6">
                                  <div className="min-w-0">
                                    <h2 className="text-[20px] font-semibold leading-tight tracking-tight text-slate-900 sm:text-[22px]">
                                      {title || "—"}
                                    </h2>
                                  </div>

                                  <div className="mt-3 grid grid-cols-2 gap-2.5">
                                    <SpecRow label="Seats">
                                      {v.seatingCapacity ?? "N/A"}
                                    </SpecRow>
                                    <SpecRow label="Color">{v.color || "N/A"}</SpecRow>
                                    {ageLabel ? <SpecRow label="Age">{ageLabel}</SpecRow> : null}
                                  </div>

                                  <div className="mt-4 grid max-w-4xl gap-3 lg:grid-cols-2">
                                    <div className="rounded-xl border border-slate-200 bg-white shadow-sm">
                                      <div className="flex items-baseline justify-between gap-3 px-4 pb-2 pt-3">
                                        <div className="text-[11px] uppercase tracking-[0.08em] text-slate-500">
                                          Total fare
                                        </div>
                                        <div className="text-2xl font-extrabold tracking-tight text-slate-900 tabular-nums">
                                          {fmtINR(totalFareOf(it))}
                                        </div>
                                      </div>
                                      <div className="border-t border-slate-100 px-4 py-3">
                                        <div className="space-y-2 text-[13px] text-slate-600">
                                          <div className="flex items-center justify-between gap-4">
                                            <span>Booking amount</span>
                                            <span className="font-semibold text-slate-900">
                                              {bookingSummary.fullyWaived ? "₹0" : fmtINR(bookingAmountOf(it))}
                                            </span>
                                          </div>
                                          <div className="flex items-center justify-between gap-4">
                                            <span>Pay after ride</span>
                                            <span className="font-semibold text-slate-900">{fmtINR(pendingCashOf(it))}</span>
                                          </div>
                                        </div>
                                      </div>
                                    </div>

                                    <div className="rounded-xl border border-slate-200 bg-white shadow-sm">
                                      <div className="px-4 pb-2 pt-3">
                                        <div className="text-[11px] uppercase tracking-[0.08em] text-slate-500">
                                          Calculation
                                        </div>
                                      </div>
                                      <div className="border-t border-slate-100 px-4 py-3">
                                        <div className="flex items-center justify-between gap-4 text-[13px] text-slate-600">
                                          <span>Per car fare</span>
                                          <span className="font-semibold text-slate-900">
                                            {fmtINR(unitFare)}
                                          </span>
                                        </div>
                                        <div className="mt-1 flex items-center justify-between gap-4 text-[13px] text-slate-600">
                                          <span>Quantity</span>
                                          <span className="font-semibold text-slate-900">
                                            {quantity} car{quantity > 1 ? "s" : ""}
                                          </span>
                                        </div>
                                        <div className="mt-1 flex items-center justify-between gap-4 text-[13px] text-slate-600">
                                          <span>Total fare</span>
                                          <span className="font-semibold text-slate-900">
                                            {fmtINR(unitFare)} x {quantity} = {fmtINR(totalFareOf(it))}
                                          </span>
                                        </div>
                                        <div className="mt-1 flex items-center justify-between gap-4 text-[13px] text-slate-600">
                                          <span>Base booking amount</span>
                                          <span className="font-semibold text-slate-900">
                                            {fmtINR(bookingSummary.originalBookingAmount)}
                                          </span>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </div>

                                <div className="border-t border-slate-200 p-4 lg:flex lg:w-[280px] lg:flex-col lg:justify-between lg:border-l lg:border-t-0 lg:p-6">
                                  <QuantityAdjuster
                                    quantity={quantity}
                                    busy={quantityBusy}
                                    onDecrease={() => updateItemQuantity(it, quantity - 1)}
                                    onIncrease={() => updateItemQuantity(it, quantity + 1)}
                                  />

                                  <button
                                    data-no-toggle="true"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleRemoveVehicle(it?.id);
                                    }}
                                    type="button"
                                    className="mt-4 inline-flex h-10 items-center justify-center self-end rounded-md border border-red-200 px-4 text-xs font-semibold text-red-700 hover:bg-red-50 active:scale-[0.99] lg:mt-6"
                                  >
                                    Remove car
                                  </button>
                                </div>
                              </div>
                            </article>
                          );
                        })}
                      </div>
                    )}
                  </article>
                );
              })}
            </div>

            {hasNonExpiredGroups ? (
              <PaymentOverviewCard
                totalFare={totalFare}
                totalBookingAmount={totalBookingAmount}
                totalPendingCash={totalPendingCash}
                totalOriginalBookingAmount={totalOriginalBookingAmount}
                discountPercent={scheduleBookingDiscountPercent}
              />
            ) : null}

            <CheckoutBar
              cartItems={activeItems}
              totalFare={totalFare}
              totalBookingAmount={totalBookingAmount}
              totalPendingCash={totalPendingCash}
              totalOriginalBookingAmount={totalOriginalBookingAmount}
              discountPercent={scheduleBookingDiscountPercent}
            />
          </>
        )}
      </main>

      {mobileStopsModal.open ? (
        <div className="fixed inset-0 z-[75] flex items-end justify-center bg-black/30 px-4 sm:hidden">
          <div className="w-full max-w-md rounded-t-[28px] bg-white px-4 pb-6 pt-4 shadow-2xl">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-[17px] font-semibold text-slate-900">Stops</div>
                <div className="mt-1 text-sm text-slate-500">
                  {mobileStopsModal.stops.length} stop{mobileStopsModal.stops.length === 1 ? "" : "s"}
                </div>
              </div>
              <button
                type="button"
                onClick={() => setMobileStopsModal({ open: false, stops: [] })}
                className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-slate-200 text-slate-700"
                aria-label="Close stops"
              >
                <Icon path={mdiClose} size={0.8} />
              </button>
            </div>

            <div className="mt-4 space-y-3">
              {mobileStopsModal.stops.map((stop, idx) => (
                <div
                  key={`${stop}-${idx}`}
                  className="rounded-2xl border border-slate-200 bg-slate-50/80 px-4 py-3"
                >
                  <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.08em] text-slate-500">
                    <Icon path={mdiMapMarkerPath} size={0.68} />
                    <span>Stop {idx + 1}</span>
                  </div>
                  <div className="mt-1 text-sm font-medium leading-snug text-slate-900">{stop}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      ) : null}

      {/* Remove plan confirmation */}
      {planToRemove && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/40 px-4">
          <div className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-5 shadow-2xl">
            <h2 className="text-[17px] font-semibold text-slate-900">
              Remove this trip plan?
            </h2>
            <p className="mt-1 text-sm text-slate-600">
              All vehicles in this plan will be removed from your cart:
            </p>
            <div className="mt-3 max-h-48 space-y-2 overflow-auto">
              {planToRemove.vehicles.map((it, idx) => {
                const v = resolveCartVehicle(it);
                const name = `${v.brand || ""} ${v.model || ""}`.trim() || "—";
                const reg = v.registrationNumber ? ` • ${v.registrationNumber}` : "";
                return (
                  <div
                    key={`${v.registrationNumber || idx}`}
                    className="flex items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-[13px] text-slate-800"
                  >
                    <span className="h-2 w-2 rounded-full bg-slate-400" />
                    <span className="truncate">
                      {name}
                      {reg}
                    </span>
                    <span className="ml-auto text-xs text-slate-500">
                      {fmtINR(totalFareOf(it))}
                    </span>
                  </div>
                );
              })}
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => {
                  if (planRemoveBusy) return;
                  setPlanToRemove(null);
                }}
                className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={actuallyRemovePlan}
                className="rounded-lg border border-red-200 bg-red-50 px-4 py-2 text-sm font-medium text-red-700 hover:bg-red-100 disabled:opacity-60"
                disabled={planRemoveBusy}
              >
                {planRemoveBusy ? "Removing…" : "Remove plan"}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="sm:hidden">
        <footer className="border-t border-slate-200 bg-white">
          <div className="mx-auto flex items-center justify-center px-4 py-5">
            <img
              src="/Wayroot (1600 x 500 px).svg"
              alt="Wayroot"
              className="h-7 w-auto"
            />
          </div>
        </footer>
      </div>
      <div className="hidden sm:block">
        <Footer />
      </div>
    </div>
  );
}
